import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Save, Store, MapPin, Phone, Mail, FileText, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    shop_name: '',
    address_street: '',
    address_zip: '',
    address_city: '',
    address_country: '',
    phone_mobile: '',
    phone_fixed: '',
    email: '',
    website: '',
    vat_number: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/shop-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/shop-settings`, settings);
      toast.success('Paramètres enregistrés avec succès');
    } catch (error) {
      console.error('Error saving settings:', error);
      if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas les droits pour modifier les paramètres');
      } else {
        toast.error('Erreur lors de l\'enregistrement des paramètres');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <Store className="w-8 h-8" />
          Paramètres du Magasin
        </h1>
        <p className="text-gray-600 mt-2">
          Configurez les informations de votre magasin qui apparaîtront sur les tickets et étiquettes
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations générales */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Informations Générales
            </CardTitle>
            <CardDescription>
              Nom et identité du magasin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="shop_name">Nom du magasin *</Label>
              <Input
                id="shop_name"
                name="shop_name"
                value={settings.shop_name}
                onChange={handleChange}
                placeholder="Smart Service Mobile"
                required
              />
            </div>

            <div>
              <Label htmlFor="vat_number">Numéro de TVA</Label>
              <Input
                id="vat_number"
                name="vat_number"
                value={settings.vat_number || ''}
                onChange={handleChange}
                placeholder="BE 0123.456.789"
              />
              <p className="text-sm text-gray-500 mt-1">
                Sera affiché sur les tickets et factures
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Adresse */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Adresse
            </CardTitle>
            <CardDescription>
              Localisation du magasin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="address_street">Rue et numéro *</Label>
              <Input
                id="address_street"
                name="address_street"
                value={settings.address_street}
                onChange={handleChange}
                placeholder="Rue de la Réparation 123"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="address_zip">Code postal *</Label>
                <Input
                  id="address_zip"
                  name="address_zip"
                  value={settings.address_zip}
                  onChange={handleChange}
                  placeholder="4000"
                  required
                />
              </div>

              <div>
                <Label htmlFor="address_city">Ville *</Label>
                <Input
                  id="address_city"
                  name="address_city"
                  value={settings.address_city}
                  onChange={handleChange}
                  placeholder="Liège"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address_country">Pays *</Label>
              <Input
                id="address_country"
                name="address_country"
                value={settings.address_country}
                onChange={handleChange}
                placeholder="Belgique"
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Contact
            </CardTitle>
            <CardDescription>
              Coordonnées de contact
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="phone_mobile">Téléphone GSM *</Label>
              <Input
                id="phone_mobile"
                name="phone_mobile"
                type="tel"
                value={settings.phone_mobile}
                onChange={handleChange}
                placeholder="+32 475 12 34 56"
                required
              />
            </div>

            <div>
              <Label htmlFor="phone_fixed">Téléphone fixe</Label>
              <Input
                id="phone_fixed"
                name="phone_fixed"
                type="tel"
                value={settings.phone_fixed || ''}
                onChange={handleChange}
                placeholder="+32 4 123 45 67"
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={settings.email}
                onChange={handleChange}
                placeholder="contact@smartservicemobile.be"
                required
              />
            </div>

            <div>
              <Label htmlFor="website">Site web</Label>
              <Input
                id="website"
                name="website"
                type="url"
                value={settings.website || ''}
                onChange={handleChange}
                placeholder="www.smartservicemobile.be"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={fetchSettings}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </form>

      {/* Information */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">💡 Information</p>
              <p>
                Ces informations seront utilisées sur tous les tickets et étiquettes générés.
                Assurez-vous qu'elles sont correctes et à jour.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShopSettings;
